import React, { useEffect, useRef, useState } from "react";
import { AlertCircle, Loader2, RefreshCw, Sparkles, X } from "lucide-react";
import { reviewPatientFinance, trackContextualAIEvent } from "../../services/contextualAiApi";


export default function FinancialAIReviewCard({ patientId, onClose }) {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const tracked = useRef(false);

  useEffect(() => {
    if (tracked.current) return;
    tracked.current = true;
    trackContextualAIEvent("ai_context_action_shown", {
      module: "finance",
      action: "operational_review",
      metadata: { context_type: "finance" },
    });
  }, []);

  const runReview = async () => {
    if (loading) return;
    setLoading(true);
    setError("");
    const startedAt = Date.now();
    trackContextualAIEvent("ai_context_action_clicked", {
      module: "finance",
      action: "operational_review",
      metadata: { context_type: "finance" },
    });
    try {
      const result = await reviewPatientFinance(patientId);
      setAnalysis(result);
      trackContextualAIEvent("ai_context_generation_success", {
        module: "finance",
        action: "operational_review",
        outcome: "rules_only",
        duration_ms: Date.now() - startedAt,
        analysis_id: result?.id,
        metadata: { context_type: "finance", status: result?.status },
      });
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "Não foi possível gerar o resumo financeiro agora.");
      trackContextualAIEvent("ai_context_generation_failed", {
        module: "finance",
        action: "operational_review",
        outcome: "error",
        duration_ms: Date.now() - startedAt,
        metadata: { context_type: "finance", error_code: String(requestError?.response?.status || "request_failed") },
      });
    } finally {
      setLoading(false);
    }
  };

  if (!analysis) {
    return (
      <div className="mb-4 rounded-xl border border-purple-200 bg-purple-50 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-purple-700" />
            <div>
              <p className="font-semibold text-gray-900">Entenda a situação financeira deste paciente</p>
              <p className="mt-1 text-sm text-gray-600">Organize vencimentos, valores em aberto e carteira sem alterar nenhum lançamento.</p>
            </div>
          </div>
          <div className="flex shrink-0 gap-2">
            <button type="button" onClick={onClose} className="rounded-lg border bg-white px-3 py-2 text-sm">Fechar</button>
            <button type="button" onClick={runReview} disabled={loading} className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Gerar resumo
            </button>
          </div>
        </div>
        {error && <p className="mt-3 text-sm text-red-700">{error}</p>}
      </div>
    );
  }

  const payload = analysis.payload || {};
  const money = (value) => (Number(value) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const generatedAt = payload.generated_snapshot_at ? new Date(payload.generated_snapshot_at).toLocaleString("pt-BR") : "";
  const attentionItems = Array.isArray(payload.attention_items) ? payload.attention_items : [];

  return (
    <div className="mb-4 rounded-xl border border-purple-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-purple-700" />
          <div>
            <p className="font-semibold text-gray-900">Resumo financeiro assistido</p>
            <p className="text-xs text-gray-500">Fotografia gerada em {generatedAt}</p>
          </div>
        </div>
        <div className="flex gap-3">
          <button type="button" onClick={runReview} disabled={loading} className="inline-flex items-center gap-1 text-xs font-medium text-purple-700"><RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Atualizar fotografia</button>
          <button type="button" onClick={onClose} aria-label="Fechar"><X className="h-4 w-4 text-gray-500" /></button>
        </div>
      </div>

      <p className="mt-3 text-sm text-gray-700">{payload.summary}</p>
      <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        <div className="rounded-lg bg-gray-50 p-3"><p className="text-xs text-gray-500">Em aberto</p><p className="font-semibold text-gray-900">{money(payload.open_amount)}</p></div>
        <div className="rounded-lg bg-red-50 p-3"><p className="text-xs text-red-700">Vencido</p><p className="font-semibold text-red-900">{money(payload.overdue_amount)}</p><p className="text-xs text-red-700">{payload.overdue_count || 0} vencimento(s)</p></div>
        <div className="rounded-lg bg-blue-50 p-3"><p className="text-xs text-blue-700">Próximos 30 dias</p><p className="font-semibold text-blue-900">{money(payload.upcoming_30_days_amount)}</p><p className="text-xs text-blue-700">{payload.upcoming_30_days_count || 0} vencimento(s)</p></div>
        <div className="rounded-lg bg-purple-50 p-3"><p className="text-xs text-purple-700">Carteira disponível</p><p className="font-semibold text-purple-900">{money(payload.wallet_available)}</p></div>
      </div>

      {attentionItems.length > 0 && <div className="mt-4 rounded-lg bg-amber-50 p-3 text-amber-900"><p className="flex items-center gap-2 text-sm font-semibold"><AlertCircle className="h-4 w-4" /> Pontos para conferência</p><ul className="mt-2 space-y-1 text-sm">{attentionItems.map((item, index) => <li key={index}>• {item}</li>)}</ul></div>}
      <p className="mt-4 text-xs text-gray-500">{payload.disclaimer}</p>
      {error && <p className="mt-3 text-sm text-red-700">{error}</p>}
    </div>
  );
}
